import React, { Fragment, useState, useRef, useLayoutEffect, useCallback } from 'react';
import { Menu, Transition } from '@headlessui/react';
import { createPortal } from 'react-dom';

const FloatingMenu = ({
    button,
    children,
    menuWidth = 192,
    placement = 'bottom-end'
}) => {
    const [coords, setCoords] = useState(null);
    const buttonRef = useRef(null);

    const updatePosition = useCallback(() => {
        if (!buttonRef.current) return;
        const rect = buttonRef.current.getBoundingClientRect();

        // Horizontal Positioning
        let left = rect.right - menuWidth; // Default align right edge
        let originX = 'right';

        if (placement === 'bottom-start') {
            left = rect.left;
            originX = 'left';
        }

        // Viewport constraints (Right)
        if (left + menuWidth > window.innerWidth) {
            left = window.innerWidth - menuWidth - 8;
            originX = 'right';
        }
        // Viewport constraints (Left)
        if (left < 8) {
            left = 8;
            originX = 'left';
        }

        // Vertical Positioning
        const spaceBelow = window.innerHeight - rect.bottom;
        const spaceAbove = rect.top;

        let top = rect.bottom + 4;
        let bottom = undefined;
        let originY = 'top';

        // Flip to top if restricted space below and more space above
        // Threshold 240px for typical menu height
        if (spaceBelow < 240 && spaceAbove > spaceBelow) {
            top = undefined;
            bottom = window.innerHeight - rect.top + 4;
            originY = 'bottom';
        }

        setCoords({
            top,
            bottom,
            left,
            origin: `origin-${originY}-${originX}`
        });
    }, [menuWidth, placement]);

    return (
        <Menu as="div" className="relative inline-block text-left">
            {({ open }) => (
                <>
                    <div ref={buttonRef} className="inline-block">
                        <Menu.Button as={Fragment} onClick={updatePosition}>
                            {button}
                        </Menu.Button>
                    </div>

                    {open && createPortal(
                        <div
                            className="fixed z-[9999]"
                            style={{
                                top: coords?.top,
                                bottom: coords?.bottom,
                                left: coords?.left,
                                opacity: coords ? 1 : 0,
                                visibility: coords ? 'visible' : 'hidden'
                            }}
                        >
                            <Transition
                                as={Fragment}
                                show={true}
                                appear={true}
                                enter="transition ease-out duration-100"
                                enterFrom="transform opacity-0 scale-95"
                                enterTo="transform opacity-100 scale-100"
                                leave="transition ease-in duration-75"
                                leaveFrom="transform opacity-100 scale-100"
                                leaveTo="transform opacity-0 scale-95"
                            >
                                <Menu.Items
                                    static
                                    className={`w-48 rounded-md bg-white shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none overflow-hidden ${coords?.origin || 'origin-top-right'}`}
                                    onKeyDown={(e) => {
                                        if (e.key === 'Tab') e.preventDefault();
                                    }}
                                >
                                    {children}
                                </Menu.Items>
                            </Transition>
                        </div>,
                        document.body
                    )}

                    {/* Scroll/Resize Listener when open */}
                    <PositionListener open={open} updatePosition={updatePosition} />
                </>
            )}
        </Menu>
    );
};

// Helper to listen for scroll/resize while menu is open
const PositionListener = ({ open, updatePosition }) => {
    useLayoutEffect(() => {
        if (!open) return;
        updatePosition();
        window.addEventListener('resize', updatePosition);
        window.addEventListener('scroll', updatePosition, true);
        return () => {
            window.removeEventListener('resize', updatePosition);
            window.removeEventListener('scroll', updatePosition, true);
        };
    }, [open, updatePosition]);
    return null;
};

export default FloatingMenu;
